import { Button } from '@shared/components/button';
import { ExpandablePanel } from '@shared/components/expandable-panel';
import { Text } from '@shared/components/text';
import { Tooltip } from '@shared/components/tooltip';
import { RatingMethod } from '@shared/generated/graphql';
import { RateMetadata } from '@shared/types/quote';
import { usdFormatter } from 'clerk_common/stringification/numbers';
import { motion } from 'framer-motion';
import { isNil } from 'lodash';
import { useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { extractTransfixPercentiles } from '../utils/extractTransfixPercentiles';
import { DATRateWidget } from './DATRateWidget';

export const SonarRateWidget = ({ metadata }: { metadata: RateMetadata }) => {
  const response = metadata?.response as {
    lookbackDays?: number;
  };
  const { lookbackDays } = response || {};

  return (
    <div className="flex w-full flex-col gap-2">
      {lookbackDays && (
        <Text type="custom" className="text-[10px] text-gray-500">
          <strong>
            Average rate based on {lookbackDays} day lookback period
          </strong>
        </Text>
      )}
    </div>
  );
};

const ConfidenceMeter = ({
  label,
  percent,
  barClassName,
}: {
  label: string;
  percent: number;
  barClassName: string;
}) => (
  <div className="flex w-full flex-col gap-1">
    <div className="flex justify-between">
      <Text type="custom" className="text-[10px] text-gray-500">
        {label}:
      </Text>
      <Text type="custom" className="text-[10px] text-gray-500">
        {percent}
      </Text>
    </div>
    <div className="h-1 w-full bg-gray-200">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 0.7 }}
        className={`h-1 ${barClassName}`}
      />
    </div>
  </div>
);

export const SonarConfidenceLevel = ({
  metadata,
}: {
  metadata: RateMetadata;
}) => {
  const response = metadata?.response as {
    confidenceScore?: number;
  };
  const { confidenceScore } = response || {};

  if (!confidenceScore) return null;

  return (
    <ConfidenceMeter
      label="Confidence level"
      percent={(confidenceScore / 5) * 100}
      barClassName="bg-gray-800"
    />
  );
};

export const DATRateStrength = ({ metadata }: { metadata: RateMetadata }) => {
  const rateStrength = metadata?.response?.rate?.rateStrength;

  if (typeof rateStrength !== 'number') return null;

  return (
    <ConfidenceMeter
      label="Rate strength"
      percent={rateStrength}
      barClassName="bg-orange-500"
    />
  );
};

export const TruckstopAdditionalData = ({
  metadata,
}: {
  metadata: RateMetadata;
}) => {
  const { dataRecordsScore, loadInputScore } = metadata?.response || {};

  if (!dataRecordsScore || !loadInputScore) return null;

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex justify-end gap-1">
        <Text type="custom" className="text-[10px] text-gray-500">
          Data records score:
        </Text>
        <Text type="custom" className="text-[10px] text-gray-500">
          <strong>{dataRecordsScore}%</strong>
        </Text>
      </div>
      <div className="flex justify-end gap-1">
        <Text type="custom" className="text-[10px] text-gray-500">
          Load input score:
        </Text>
        <Text type="custom" className="text-[10px] text-gray-500">
          <strong>{loadInputScore}%</strong>
        </Text>
      </div>
    </div>
  );
};

export const TabiRateWidget = ({ metadata }: { metadata: RateMetadata }) => {
  const identifiers = metadata?.response?.identifiers as string[];
  if (!identifiers?.length) return null;

  return (
    <Text type="custom" className="mt-2 text-[10px] text-gray-500">
      Rate requested with customer identifier{identifiers.length > 1 ? 's' : ''}
      : <strong>{identifiers.join(', ')}</strong>
    </Text>
  );
};

export const NTGConfidenceLevel = ({
  metadata,
}: {
  metadata: RateMetadata;
}) => {
  const confidenceLevel = metadata?.response?.Confidence.confidence_score;

  if (!confidenceLevel) return null;

  return (
    <ConfidenceMeter
      label="Confidence level"
      percent={confidenceLevel}
      barClassName="bg-gray-700"
    />
  );
};

export const TriumphConfidenceLevel = ({
  metadata,
}: {
  metadata: RateMetadata;
}) => {
  const confidenceLevel = metadata?.response?.confidenceLevel;

  if (!confidenceLevel) return null;

  return (
    <ConfidenceMeter
      label="Confidence level"
      percent={confidenceLevel}
      barClassName="bg-cyan-500"
    />
  );
};

export const TransfixConfidenceLevel = ({
  metadata,
}: {
  metadata: RateMetadata;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const data = extractTransfixPercentiles(metadata);
  return (
    <ExpandablePanel
      summary={
        <div>
          <Button
            size="xs"
            variant="tertiary"
            onPress={() => setIsExpanded(!isExpanded)}
            icon={isExpanded ? <MdExpandLess /> : <MdExpandMore />}
          >
            Confidence
          </Button>
        </div>
      }
      expanded={isExpanded}
      className="w-full"
      setExpanded={setIsExpanded}
    >
      <div className="no-scrollbar flex w-full flex-col">
        {data.map((element) => {
          const opacity = (parseInt(element.opacity) + 50) / 150;
          const value = usdFormatter.format(element.value);
          return (
            <Tooltip
              content={`${element.label} - ${value}`}
              key={element.key}
              side="left"
            >
              <div
                className="text- flex w-full cursor-pointer flex-row justify-start px-1 py-[2px]"
                style={{
                  backgroundColor: `rgba(22,163,74, ${opacity})`,
                }}
              >
                <Text type="custom" className="text-[10px] text-white">
                  {element.label} - {value}
                </Text>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </ExpandablePanel>
  );
};

export const EchoConfidenceLevel = ({
  metadata,
}: {
  metadata: RateMetadata;
}) => {
  const confidenceLevel = metadata?.response?.rate?.buyRateConfidence;

  if (isNil(confidenceLevel)) return null;

  const confidenceLevelDisplay = (
    <Text type="custom" className="text-[11px] text-gray-500">
      Confidence: {confidenceLevel.confidenceScore}
    </Text>
  );

  const rangeDisplay = areRangesMeaningful(confidenceLevel) ? (
    <Text type="custom" className="text-[11px] text-gray-500">
      ${confidenceLevel.rangeLower} - ${confidenceLevel.rangeUpper}
    </Text>
  ) : null;

  return (
    <div className="flex w-full flex-col gap-1">
      {confidenceLevelDisplay}
      {rangeDisplay}
    </div>
  );
};

type EchoConfidence = {
  confidenceScore: number;
  rangeLower: number;
  rangeUpper: number;
};

const areRangesMeaningful = (confidence: EchoConfidence) => {
  // NOTE(jacob): Echo (at least on their dev server) sometimes returns
  // -9999, which I'm assuming is a placeholder.
  return confidence.rangeLower > 0 && confidence.rangeUpper > 0;
};

export const getRateConfidence = ({
  ratingMethod,
  metadata,
}: {
  ratingMethod?: RatingMethod;
  metadata?: RateMetadata;
}) => {
  switch (ratingMethod) {
    case RatingMethod.GREENSCREENS:
      return metadata ? <TriumphConfidenceLevel metadata={metadata} /> : null;
    case RatingMethod.ECHO:
      return metadata ? <EchoConfidenceLevel metadata={metadata} /> : null;
    case RatingMethod.NTG:
      return metadata ? <NTGConfidenceLevel metadata={metadata} /> : null;
    case RatingMethod.TRANSFIX:
      return metadata ? <TransfixConfidenceLevel metadata={metadata} /> : null;
    case RatingMethod.SONAR:
      return metadata ? <SonarConfidenceLevel metadata={metadata} /> : null;
    case RatingMethod.DAT:
      return metadata &&
        typeof metadata.response?.rate?.rateStrength === 'number' ? (
        <DATRateStrength metadata={metadata} />
      ) : null;
    default:
      return null;
  }
};

export const AVRLAdditionalData = ({
  metadata,
}: {
  metadata: RateMetadata;
}) => {
  const additionalData = metadata?.response?.additionalData;

  if (!hasAVRLMarketContext(additionalData)) {
    return null;
  }

  const { escalation } = additionalData.dat;

  return (
    <div className="flex w-full flex-col gap-1">
      <Text type="custom" className="mt-2 text-[12px] text-gray-500">
        Market context
      </Text>
      <div>
        <Text type="custom" className="text-[10px]">
          <strong>Origin:</strong> {escalation.origin.name}
        </Text>
        <Text type="custom" className="text-[10px]">
          <strong>Destination:</strong> {escalation.destination.name}
        </Text>
      </div>
    </div>
  );
};

type AVRLMarketContext = {
  dat: {
    escalation: {
      origin: { name: string };
      destination: { name: string };
    };
  };
};

const hasAVRLMarketContext = (
  additionalData: unknown
): additionalData is AVRLMarketContext => {
  const context = additionalData as Partial<AVRLMarketContext> | undefined;
  return (
    typeof context?.dat?.escalation?.origin?.name === 'string' &&
    typeof context.dat.escalation.destination?.name === 'string'
  );
};

export const getAdditionalRateData = ({
  ratingMethod,
  metadata,
}: {
  ratingMethod?: RatingMethod;
  metadata?: RateMetadata;
}) => {
  switch (ratingMethod) {
    case RatingMethod.TRUCKSTOP:
      return metadata ? <TruckstopAdditionalData metadata={metadata} /> : null;
    default:
      return null;
  }
};

export const RateWidget = ({
  ratingMethod,
  metadata,
}: {
  ratingMethod?: RatingMethod;
  metadata?: RateMetadata;
}) => {
  switch (ratingMethod) {
    case RatingMethod.GREENSCREENS:
      return null;
    case RatingMethod.DAT:
      return metadata ? <DATRateWidget metadata={metadata} /> : null;
    case RatingMethod.TABI:
      return metadata ? <TabiRateWidget metadata={metadata} /> : null;
    case RatingMethod.AVRL:
      return metadata ? <AVRLAdditionalData metadata={metadata} /> : null;
    case RatingMethod.SONAR:
      return metadata ? <SonarRateWidget metadata={metadata} /> : null;
    default:
      return null;
  }
};
